import { useContext } from 'react';
import { UseMutateFunction } from 'react-query';
import moment from 'moment';

import { ISelectModel } from 'common/models';
import { ReservationLocalstorageSteps } from 'modules/accommodations/features/reservations/components/ReservationAddNew/ReservationSteps/consts';
import {
  calculatedAmountForServiceTable,
  constructPayloadForAPI,
  localStorageValuesFromFirstStep,
  transformedServicesForTable,
} from 'modules/accommodations/features/reservations/helper';
import {
  useCalculationServiceTypesQuery,
  useTransitionInStepsTransformedTableData,
} from 'modules/accommodations/features/reservations/hooks';
import { TApiData, TServiceTableData } from 'modules/accommodations/features/reservations/models';
import { ReservationNewContext } from 'modules/accommodations/features/reservations/routes/ReservationNewContext';

export const useServiceSidebarModalHelper = (
  upsertServicesMutate: UseMutateFunction<any, unknown, TApiData, unknown>,
) => {
  const { serviceTypes, unitOfMeasures } = useCalculationServiceTypesQuery({});

  const { thirdStepTableState, setThirdStepTableState } = useContext(ReservationNewContext);
  const { servicesTableData, discountsTableData, isServiceSideBarOpen, rowIndex } =
    thirdStepTableState;

  const localstorageFirstStep = JSON.parse(
    localStorage.getItem(ReservationLocalstorageSteps.FIRSTSTEP) as string,
  );
  const { from, to, numberOfGuestsSum, unitId } =
    localStorageValuesFromFirstStep(localstorageFirstStep);

  const { transformedServicesDataForAPI, transformedDiscountsDataForAPI } =
    useTransitionInStepsTransformedTableData(
      servicesTableData,
      discountsTableData,
      from,
      to,
      numberOfGuestsSum,
      isServiceSideBarOpen,
    );

  const addNewService = (values: any) => {
    const startDate = moment(from);
    const endDate = moment(to);
    const numberOfDays = endDate.diff(startDate, 'days');
    const calculatedAmountPerUnitOfMeasure = calculatedAmountForServiceTable(
      values.unitOfMeasureId,
      numberOfGuestsSum as number,
      numberOfDays,
    );

    //exclude adults, babies, children etc. props from object "values"
    const { adults, babies, children, teens, ...restProps } = values;
    const formData = {
      ...restProps,
      amount: calculatedAmountPerUnitOfMeasure,
      totalSellingAmountPerUnitOfMeasure:
        numberOfGuestsSum * restProps.sellingPricePerUnitOfMeasure,
    };
    const mergeNewServices = transformedServicesDataForAPI.concat([formData]);

    const apiPayload: TApiData = constructPayloadForAPI(
      unitId,
      mergeNewServices,
      transformedDiscountsDataForAPI,
    );
    upsertServicesMutate(apiPayload);

    //data for table services state
    const transformedServices: TServiceTableData = transformedServicesForTable(
      values,
      serviceTypes as ISelectModel<number>[],
      unitOfMeasures as ISelectModel<number>[],
      from,
      to,
      numberOfGuestsSum,
      servicesTableData,
    );
    const mergeTransformedServices = servicesTableData.concat([transformedServices]);
    setThirdStepTableState({
      servicesTableData: mergeTransformedServices,
      isServiceSideBarOpen: false,
    });

    //save new services and discounts to localStorage
    const { calculationServices, calculationDiscountServices } = apiPayload;
    const localStorageData = {
      services: mergeTransformedServices,
      discountServices: discountsTableData,
      servicesStepFour: calculationServices,
      discountServicesStepFour: calculationDiscountServices,
    };
    localStorage.setItem(ReservationLocalstorageSteps.THIRDSTEP, JSON.stringify(localStorageData));
  };

  const editService = (values: any) => {
    //edited data for API
    const { adults, babies, children, teens, languageId, ...restProps } = values;
    const mergeEditedServices = [...transformedServicesDataForAPI];
    mergeEditedServices[rowIndex as number] = restProps;

    const apiEditedPayload: TApiData = constructPayloadForAPI(
      unitId,
      mergeEditedServices,
      transformedDiscountsDataForAPI,
    );
    upsertServicesMutate(apiEditedPayload);

    //edited data for table services state
    const transformedEditedService: TServiceTableData = transformedServicesForTable(
      values,
      serviceTypes as ISelectModel<number>[],
      unitOfMeasures as ISelectModel<number>[],
      from,
      to,
      numberOfGuestsSum,
      servicesTableData,
    );
    const mergeEditedTransformedServices = [...servicesTableData];
    mergeEditedTransformedServices[rowIndex as number] = transformedEditedService;

    setThirdStepTableState({
      servicesTableData: mergeEditedTransformedServices,
      isServiceSideBarOpen: false,
    });

    //save edited services and discounts to localStorage
    const { calculationServices, calculationDiscountServices } = apiEditedPayload;
    const localStorageData = {
      services: mergeEditedTransformedServices,
      discountServices: discountsTableData,
      servicesStepFour: calculationServices,
      discountServicesStepFour: calculationDiscountServices,
    };
    localStorage.setItem(ReservationLocalstorageSteps.THIRDSTEP, JSON.stringify(localStorageData));
  };

  return { addNewService, editService };
};
